import { decodePagePayload, firstUrl } from '@closetly/core';
import { ClientResponseError } from 'pocketbase';
import { useEffect, useState } from 'react';
import { useAuthUser } from './lib/auth';
import { pb } from './lib/pb';
import { LoginPage } from './components/LoginPage';
import type { SharedLink } from './components/ItemSheet';
import { SharePage } from './components/SharePage';
import { Shell } from './components/Shell';

/**
 * Two ways in: Android opens /share?title=…&text=…&url=… when a link is
 * shared to the installed app (share_target in the manifest; shop apps often
 * put the link into `text`), and the browser button opens /share#page=… with
 * the product data of the page it was tapped on.
 */
function takeSharedLink(): SharedLink | null {
  if (location.pathname !== '/share') return null;
  const encoded = new URLSearchParams(location.hash.slice(1)).get('page');
  const page = encoded ? decodePagePayload(encoded) : null;
  if (page) {
    history.replaceState(null, '', '/');
    return { url: page.url, text: '', page };
  }
  const q = new URLSearchParams(location.search);
  const text = [q.get('title'), q.get('text')].filter(Boolean).join(' ');
  const url = q.get('url') || firstUrl(text);
  history.replaceState(null, '', '/');
  return url ? { url, text } : null;
}

export function App({ onLogout }: { onLogout: () => void }) {
  const user = useAuthUser();
  // kept across the login page, so a share before signing in isn't lost
  const [shared] = useState(takeSharedLink);

  // Extend the session on start; drop it only if the server rejects it.
  useEffect(() => {
    if (!pb.authStore.isValid || !navigator.onLine) return;
    pb.collection('users')
      .authRefresh()
      .catch((err) => {
        if (err instanceof ClientResponseError && (err.status === 401 || err.status === 404)) onLogout();
      });
  }, [onLogout]);

  // a shared link works for everyone, signed in or not
  const shareToken = location.pathname.match(/^\/s\/([A-Za-z0-9]+)\/?$/)?.[1];
  if (shareToken) return <SharePage token={shareToken} />;

  if (!user) return <LoginPage />;
  return <Shell key={user.id} user={user} onLogout={onLogout} shared={shared} />;
}
